import React, { useEffect, useMemo, useRef, useState } from "react";
import "./dashboard.scss";
import ApexCharts from "apexcharts";
import fa from "apexcharts/dist/locales/fa.json";
import Moment from "jalali-moment";

import Map from "../../../components/map/map";
import { HandleErrors } from "../../../utils/handleErrors";
import {
  getEventCountPerEventTypeService,
  getRiskPerDayService,
  getTopRiskAppVersionService,
  getTopRiskDeviceModelService,
  getTopRiskDeviceService,
} from "../../../services/event.service";
import {
  Col,
  Divider,
  Row,
  Select,
  Typography,
  Breakpoint,
  Grid,
  Tabs,
  Button,
  Tooltip,
} from "antd";
import { GetApplicationRes } from "../../../interfaces/application.interface";
import { getAllApplicationsService } from "../../../services/application.service";
import { FilterSelect } from "../../../components/filterSelect/filterSelect";
import { BounceLoader } from "react-spinners";
import { useSearchParams } from "react-router-dom";
import TabPane from "antd/lib/tabs/TabPane";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import { RootState } from "../../../redux/store";
import { useSelector } from "react-redux";

const {useBreakpoint} = Grid

const Dashboard = () => {
  const health = useSelector<RootState, string>(
    (root) => root.app.health
  );
    
  const screens = useBreakpoint();
  const fullScreenHandle = useFullScreenHandle();

  const [isReady, setIsReady] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const [daysAgo, setDaysAgo] = useState(30);

  const [barChart, setBarChart] = useState<any>(null);
  const [lineChart, setLineChart] = useState<any>(null);
  const [pieChart, setPieChart] = useState<any>(null);

  const [topRiskDevice, setTopRiskDevice] = useState<any[]>([]);
  const [topRiskDeviceModel, setTopRiskDeviceModel] = useState<any[]>([]);

  const [applications, setApplications] = useState<GetApplicationRes[]>([]);

  const [application, setApplication] = useState<GetApplicationRes>();
  const [eventType, setEventType] = useState("");

  const [isLiveRunning, setIsLiveRunning] = useState(false);
  const intervalRef = useRef<any>(null);

  useEffect(() => {
    if (isLiveRunning) {
      intervalRef.current = setInterval(() => {
        if(health != 'license-false'){
          getEventCountPerEventType();
          getRiskPerDay();
          getTopRiskDevice();
          getTopRiskAppVersion();
          getTopRiskDeviceModel();
        }
        
      }, 5000);
    } else {
      clearInterval(intervalRef.current);
    }
    // Cleanup on unmount or when isRunning changes
    return () => clearInterval(intervalRef.current);
  }, [isLiveRunning, eventType, application, health]);

  useEffect(() => {
    getApplications();

    if (searchParams.get("daysAgo")) {
      const temp = searchParams.get("daysAgo");
      if (temp) {
        setDaysAgo(parseInt(temp));
      }
    }

    if (searchParams.get("eventType")) {
      const temp = searchParams.get("eventType");
      if (temp) {
        setEventType(temp);
      }
    }

    if (searchParams.get("application")) {
      const temp = searchParams.get("application");
      if (temp) {
        setApplication({
          id: 0,
          package_name: temp,
          created_at: "",
          name: "",
          public_key: "",
        });
      }
    }

    setIsReady(true);
  }, []);

  useEffect(() => {
    if (daysAgo == 0) {
      setIsLiveRunning(true);
    } else {
      setIsLiveRunning(false);
    }
  }, [daysAgo]);

  useEffect(() => {
    let f = {
      daysAgo: daysAgo.toString(),
      eventType: "",
      application: "",
    };

    if (eventType) {
      f.eventType = eventType;
    }

    if (application) {
      f.application = application?.package_name.toString();
    }

    setSearchParams(f);

    if (isReady && health != 'license-false') {
      getEventCountPerEventType();
      getRiskPerDay();
      getTopRiskDevice();
      getTopRiskAppVersion();
      getTopRiskDeviceModel();
    }
  }, [daysAgo, application, eventType, isReady, health]);

  const getApplications = async () => {
    try {
      const res = await getAllApplicationsService({});
      setApplications(res.data);
    } catch (error) {
      HandleErrors(error);
    }
  };

  const getRiskPerDay = async () => {
    try {
      const res = await getRiskPerDayService(
        daysAgo == 0 ? 1 : daysAgo,
        application ? application?.package_name : "",
        eventType
      );
      renderLineChart(res);
    } catch (error) {
      HandleErrors(error);
    }
  };

  const getEventCountPerEventType = async () => {
    try {
      const res = await getEventCountPerEventTypeService(
        daysAgo == 0 ? 1 : daysAgo,
        application ? application?.package_name : ""
      );
      renderBarChart(res);
    } catch (error) {
      HandleErrors(error);
    }
  };

  const getTopRiskAppVersion = async () => {
    try {
      const res = await getTopRiskAppVersionService(
        daysAgo == 0 ? 1 : daysAgo,
        application ? application?.package_name : "",
        eventType
      );
      renderPieChart(res);
    } catch (error) {
      HandleErrors(error);
    }
  };

  const getTopRiskDevice = async () => {
    try {
      const res = await getTopRiskDeviceService(
        daysAgo == 0 ? 1 : daysAgo,
        application ? application?.package_name : "",
        eventType
      );
      setTopRiskDevice(res);
    } catch (error) {
      HandleErrors(error);
    }
  };

  const getTopRiskDeviceModel = async () => {
    try {      
      const res = await getTopRiskDeviceModelService(
        daysAgo == 0 ? 1 : daysAgo,
        application ? application?.package_name : "",
        eventType
      );
      setTopRiskDeviceModel(res);
    } catch (error) {
      HandleErrors(error);
    }
  };

  const renderBarChart = (data: any) => {
    const events = [];
    const counts = [];

    for (const item of data) {
      events.push(item.event_type);
      counts.push(item.event_count);
    }

    var options = {
      series: [
        {
          name: "تعداد رخداد ها",
          data: counts,
        },
      ],
      colors: ["#fe7c00"],
      fill: {
        type: "gradient",
        gradient: {
          shade: "light",
          type: "vertical",
          shadeIntensity: 0.4,
          gradientToColors: undefined, // optional, if not defined - uses the shades of same color in series
          inverseColors: true,
          opacityFrom: 1,
          opacityTo: 1,
          stops: [0, 50, 100],
          colorStops: [],
        },
      },
      chart: {
        height: '100%',
        type: "bar",
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 5,
          dataLabels: {
            position: "top", // top, center, bottom
          },
        },
      },
      dataLabels: {
        enabled: true,

        offsetY: -22,
        style: {
          fontSize: "12px",
          colors: ["#304758"],
          fontFamily: "Yekan Bakh FaNum",
        },
      },

      xaxis: {
        categories: events,
        position: "bottom",
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        crosshairs: {
          fill: {
            type: "gradient",
            gradient: {
              colorFrom: "#D8E3F0",
              colorTo: "#BED1E6",
              stops: [0, 100],
              opacityFrom: 0.1,
              opacityTo: 0.9,
            },
          },
        },
        tooltip: {
          enabled: true,
        },
        labels: {
          rotate: -60,
        },
      },
      yaxis: {
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        labels: {
          show: false,
        },
      },
      title: {
        text: "",
        floating: true,
        offsetY: 330,
        align: "center",
        style: {
          color: "#444",
        },
      },
      tooltip: {
        style: {
          fontFamily: "Yekan Bakh FaNum",
        },
      },
    };

    if (barChart) {
      barChart.updateOptions(options);
    } else {
      var chart = new ApexCharts(document.querySelector("#bar-chart"), options);
      chart.render();
      setBarChart(chart);
    }
  };

  const renderLineChart = (data: any) => {
    const dates = [];
    const devices = [];
    const risks = [];

    for (const item of data) {
      dates.push(
        Moment(item.date, "YYYY-MM-DDTHH:mm:ssZ").format(daysAgo == 0 ? "jYYYY-jMM-jDD HH:mm:ss" : "jYYYY-jMM-jDD")
      );
      devices.push(item.device_count);
      risks.push(item.total_risk);
    }

    var options = {
      chart: {
        height: '100%',
        type: "area",
        locales: [fa],
        defaultLocale: "fa",
        toolbar: {
          show: true,
          tools: {
            download: false,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true,
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      series: [
        {
          name: "تعداد دستگاه ها",
          data: devices,
        },
        {
          name: "میزان ریسک",
          data: risks,
        },
      ],
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.5,
          opacityTo: 0.9,
          stops: [0, 90, 100],
        },
      },
      xaxis: {
        categories: dates,
      },
      tooltip: {
        style: {
          fontFamily: "Yekan Bakh FaNum",
          direction: "rtl",
        },
      },
    };

    if (lineChart) {
      lineChart.updateOptions(options);
    } else {
      var chart = new ApexCharts(
        document.querySelector("#line-chart"),
        options
      );
      chart.render();
      setLineChart(chart);
    }
  };

  const renderPieChart = (data: any) => {
    const versions: any = [];
    const risks = [];

    for (const item of data) {
      versions.push(item.app_version);
      risks.push(item.total_risk);
    }

    var options = {
      series: risks,
      chart: {
        width: "100%",
        height: "80%",
        type: "pie",
      },
      labels: versions,
      theme: {
        monochrome: {
          enabled: true,
          color: "#fe7c00",
        },
      },
      plotOptions: {
        pie: {
          dataLabels: {
            offset: -5,
          },
        },
      },

      grid: {
        padding: {
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
        },
      },
      dataLabels: {},
      legend: {
        show: false,
      },

      tooltip: {
        custom: (p: any) => {
          const { series, seriesIndex, dataPointIndex, w } = p;
          return (
            '<div class="arrow_box pie-chart-custom-css">' +
            '<span class="title"> مقدار ریسک: <span class="value">' +
            series[seriesIndex] +
            "</span> </span> <br/>" +
            '<span class="title"> ورژن اپلیکیشن: <span class="value">' +
            versions[seriesIndex] +
            "</span></span>" +
            "</div>"
          );
        },
      },
    };

    if (pieChart) {
      pieChart.updateOptions(options);
    } else {
      var chart = new ApexCharts(document.querySelector("#pie-chart"), options);
      chart.render();
      setPieChart(chart);
    }
  };

  return (
    <div className="Dashboard">
      {health == 'license-false' && <div style={{
          position: 'absolute',
          zIndex: '100000',
          width: '100%',
          height: '100%',
          top: 0,
          right: 0,
          backdropFilter: 'blur(3px)',
          backgroundColor: 'rgba(255, 255, 255, 0.5)' /* optional tint */
        }}>
      </div>}
      <FullScreen handle={fullScreenHandle} className={fullScreenHandle.active ? "fullscreen": ""}>
        <Row align={"middle"} justify={"space-between"} className="title-box">
          <Col
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            {isLiveRunning && <BounceLoader size={15} color="#023FA0" />}
            <Typography.Text style={{ marginRight: 5 }} className="title">
              داشبورد
            </Typography.Text>
          </Col>
          <Col xs={24} md={fullScreenHandle.active ? 1 : 13} className="select-box">
            <Row
              align={"bottom"}
              justify={"space-between"}
              style={{
                width: "100%",
              }}
            >
              {!fullScreenHandle.active && <Col xs={24} md={9} className="box">
                <Typography.Text className="title">رخداد</Typography.Text>
                <FilterSelect
                  showAll={true}
                  fieldName="event_type"
                  value={eventType}
                  onChange={(e) => {
                    setEventType(e);
                  }}
                />
              </Col>}
              {!fullScreenHandle.active && <Col xs={24} md={6} className="box">
                <Typography.Text className="title">اپلیکیشن</Typography.Text>
                <Select
                  variant="filled"
                  className="select-comp"
                  value={application ? application?.package_name : "all"}
                  onChange={(e) => {
                    const app = applications.find((i) => i.package_name == e);
                    if (app) {
                      setApplication(app);
                    } else {
                      setApplication(undefined);
                    }
                  }}
                >
                  <Select.Option key={"all"} value={undefined} label={"همه"}>
                    همه
                  </Select.Option>
                  {applications.map((i) => {
                    return (
                      <Select.Option
                        key={i.package_name}
                        value={i.package_name}
                        label={i.name}
                      >
                        {i.name}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Col>}
              {!fullScreenHandle.active && <Col xs={24} md={6} className="box">
                <Typography.Text className="title">زمان</Typography.Text>
                <Select
                  variant="filled"
                  className="select-comp"
                  value={daysAgo}
                  onChange={(e) => {
                    setDaysAgo(e);
                  }}
                  options={[
                    { value: 0, label: "زنده" },
                    { value: 3, label: "۳ روز گذشته" },
                    { value: 7, label: "۷ روز گذشته" },
                    { value: 30, label: "ا ماه گذشته" },
                    { value: 90, label: "۳ ماه گذشته" },
                    { value: 180, label: "۶ ماه گذشته" },
                    { value: 365, label: "۱ سال گذشته" },
                  ]}
                />
              </Col>}
            {screens.md && <Col>
              <Tooltip title="Fullscreen">
                <Button
                  
                  className="fullscreen-btn"
                  type="text"
                  onClick={() => {
                    if (fullScreenHandle.active) {
                      fullScreenHandle.exit();
                    } else {
                      fullScreenHandle.enter();
                    }
                  }}
                >
                  {!fullScreenHandle.active ? (
                    <i className="las la-expand-arrows-alt icon"></i>
                  ) : (
                    <i className="las la-compress-arrows-alt icon"></i>
                  )}
                </Button>
              </Tooltip>
                
              </Col>}
            </Row>
          </Col>
        </Row>
        <Row justify={"start"} align={"stretch"}>
          <Col xs={24} md={6}>
            <div className="main-box">
              <Row
                align={"middle"}
                justify={"start"}
                className="chart-title-box"
              >
                <Col>
                  <Typography.Text className="title">
                    تعداد رخداد ها در هر استان
                  </Typography.Text>
                </Col>
              </Row>
              <Map
                key={"map"}
                eventType={eventType}
                application={application}
                daysAgo={daysAgo}
              />
            </div>
          </Col>
          <Col xs={24} md={6}>
            <div className="main-box">
              <Row
                align={"middle"}
                justify={"start"}
                className="chart-title-box"
              >
                <Col>
                  <Typography.Text className="title">
                    ۱۰ ورژن اپلیکیشن با بالاترین میزان ریسک
                  </Typography.Text>
                </Col>
              </Row>
              <div id="pie-chart"></div>
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div className="main-box">
              <Row
                align={"middle"}
                justify={"start"}
                className="chart-title-box"
              >
                <Col>
                  <Typography.Text className="title">
                    میزان ریسک و تعداد دستگاه ها به تفکیک روز
                  </Typography.Text>
                </Col>
              </Row>

              <div id="line-chart"></div>
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div className="main-box">
              <Row
                align={"middle"}
                justify={"start"}
                className="chart-title-box"
              >
                <Col>
                  <Typography.Text className="title">
                    تعداد رخداد ها به تفکیک نوع رخداد
                  </Typography.Text>
                </Col>
              </Row>
              <div id="bar-chart"></div>
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div
              className="main-box"
              style={{
                padding: "0 10px",
              }}
            >
              <Tabs defaultActiveKey="department" onChange={() => {}}>
                <TabPane key={"tab1"} tab="۱۰ دستگاه با بالاترین میزان ریسک">
                  <div className="topDevice">
                    <Row justify={"space-between"} className="title-box">
                      <Col xs={4}>
                        <Typography.Text className="title">
                          شناسه دستگاه
                        </Typography.Text>
                      </Col>
                      <Col xs={4}>
                        <Typography.Text className="title">
                          {" "}
                          آدرس IP
                        </Typography.Text>
                      </Col>
                      <Col xs={2}>
                        <Typography.Text className="title">
                          میزان ریسک
                        </Typography.Text>
                      </Col>
                      <Col xs={2}>
                        <Typography.Text className="title">
                          ورژن اندروید
                        </Typography.Text>
                      </Col>
                      <Col xs={2}>
                        <Typography.Text className="title">
                          ورژن اپلیکیشن
                        </Typography.Text>
                      </Col>
                      <Col xs={4}>
                        <Typography.Text className="title">
                          مدل دستگاه
                        </Typography.Text>
                      </Col>
                      <Col xs={3}>
                        <Typography.Text className="title">
                          نام پکیج
                        </Typography.Text>
                      </Col>
                    </Row>
                    {topRiskDevice.map((item) => {
                      return (
                        <Row justify={"space-between"} className="data-box">
                          <Col xs={4}>
                            <Typography.Text className="value">
                              {item.device_id}
                            </Typography.Text>
                          </Col>
                          <Col xs={4}>
                            <Typography.Text className="value">
                              {item.request_id}
                            </Typography.Text>
                          </Col>
                          <Col xs={2}>
                            <Typography.Text className="value">
                              {item.total_risk}
                            </Typography.Text>
                          </Col>
                          <Col xs={2}>
                            <Typography.Text className="value">
                              {item.android_version}
                            </Typography.Text>
                          </Col>
                          <Col xs={2}>
                            <Typography.Text className="value">
                              {item.app_version}
                            </Typography.Text>
                          </Col>
                          <Col xs={4}>
                            <Typography.Text className="value">
                              {item.device_manufacturer} {item.device_model}
                            </Typography.Text>
                          </Col>
                          <Col xs={3}>
                            <Typography.Text className="value">
                              {item.package_name}
                            </Typography.Text>
                          </Col>
                        </Row>
                      );
                    })}
                  </div>
                </TabPane>
                <TabPane
                  key={"tab2"}
                  tab="۱۰ مدل دستگاه با بالاترین میزان ریسک"
                >
                  <div className="topDevice">
                    <Row justify={"start"} className="title-box">
                      <Col xs={4}>
                        <Typography.Text className="title">
                          شرکت سازنده{" "}
                        </Typography.Text>
                      </Col>
                      <Col xs={7}>
                        <Typography.Text className="title">
                          مدل دستگاه
                        </Typography.Text>
                      </Col>
                      <Col xs={3}>
                        <Typography.Text className="title">
                          میزان ریسک
                        </Typography.Text>
                      </Col>
                    </Row>
                    {topRiskDeviceModel.map((item) => {
                      return (
                        <Row justify={"start"} className="data-box">
                          <Col xs={4}>
                            <Typography.Text className="value">
                              {item.device_manufacturer}
                            </Typography.Text>
                          </Col>
                          <Col xs={7}>
                            <Typography.Text className="value">
                              {item.device_model}
                            </Typography.Text>
                          </Col>
                          <Col xs={3}>
                            <Typography.Text className="value">
                              {item.total_risk}
                            </Typography.Text>
                          </Col>
                        </Row>
                      );
                    })}
                  </div>
                </TabPane>
              </Tabs>
            </div>
          </Col>
        </Row>
      </FullScreen>
    </div>
  );
};

export default Dashboard;
